import { useEffect, useState } from "react";
import { api, EvidenceBadge, PILLARS, FORMATS } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Trash2, RefreshCcw, X } from "lucide-react";

export default function CalendarView() {
  const [entries, setEntries] = useState([]);
  const [month, setMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/calendar_entries").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const days = new Date(y, m, 0).getDate();
  const offset = first.getDay();
  const grid = Array.from({ length: offset + days }, (_, i) => i < offset ? null : i - offset + 1);
  while (grid.length % 7) grid.push(null);

  const monthEntries = entries.filter((e) => e.publish_date?.startsWith(month));
  const byDay = {};
  monthEntries.forEach((e) => { const d = parseInt(e.publish_date.split("-")[2]); (byDay[d] = byDay[d] || []).push(e); });

  const pillarCounts = {};
  monthEntries.forEach((e) => { pillarCounts[e.pillar] = (pillarCounts[e.pillar] || 0) + 1; });
  const total = monthEntries.length;
  const warnings = [];
  Object.entries(pillarCounts).forEach(([p, c]) => {
    if (total > 5 && c / total > 0.5) warnings.push(`Pillar "${PILLARS[p] || p}" is overrepresented (${Math.round(c / total * 100)}%)`);
  });

  const generateScript = async (entry) => {
    setBusyId(entry.id);
    try {
      const { data } = await api.post(`/calendar_entries/${entry.id}/generate_script`);
      toast.success("Script created");
      setSelected(null);
      window.location.href = `/scripts/${data.id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Script generation failed");
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this calendar entry?")) return;
    await api.delete(`/calendar_entries/${id}`);
    toast.success("Deleted");
    setSelected(null);
    load();
  };

  const clearMonth = async () => {
    if (!confirm(`Delete all ${monthEntries.length} entries for ${month}?`)) return;
    await Promise.all(monthEntries.map((e) => api.delete(`/calendar_entries/${e.id}`)));
    toast.success(`Deleted ${monthEntries.length} entries`);
    load();
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Calendar</div>
          <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{month}</h1>
          <div className="text-sm text-neutral-600 mt-2">{monthEntries.length} posts scheduled · click a card to open</div>
        </div>
        <div className="flex gap-2">
          <input data-testid="month-picker" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-neutral-300 rounded-md px-3 py-2 text-sm" />
          {monthEntries.length > 0 && (
            <button data-testid="clear-month-btn" onClick={clearMonth} className="text-xs font-semibold px-3 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3 h-3" />Clear month</button>
          )}
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="mb-4 bg-orange-50 border border-orange-300 rounded-lg p-3">
          {warnings.map((w, i) => <div key={i} className="text-xs text-orange-900">⚠ {w}</div>)}
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold px-3 py-2 border-b border-neutral-200">{d}</div>
          ))}
          {grid.map((d, i) => (
            <div key={i} className="min-h-[120px] border-b border-r border-neutral-100 p-2">
              {d && <div className="text-xs text-neutral-400 font-semibold mb-1">{d}</div>}
              {byDay[d]?.map((e) => (
                <button key={e.id} onClick={() => setSelected(e)} data-testid={`cal-entry-${e.id}`} className="w-full text-left mb-1 p-1.5 bg-neutral-50 border border-neutral-200 rounded text-[10px] leading-tight hover:bg-neutral-100 transition-colors">
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-neutral-500 truncate">{FORMATS[e.format] || e.format}</div>
                  <EvidenceBadge confidence={e.confidence} />
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Entry drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg border border-neutral-200 p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="entry-drawer">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{PILLARS[selected.pillar] || selected.pillar} · {FORMATS[selected.format] || selected.format}</div>
                <div className="font-black text-2xl tracking-tight mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{selected.title}</div>
              </div>
              <button data-testid="close-drawer" onClick={() => setSelected(null)} className="text-neutral-500 hover:text-neutral-900"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-neutral-700 mb-3">Hook: <span className="italic">{selected.hook || "—"}</span></div>
            <div className="text-xs text-neutral-500 mb-4">Publish {selected.publish_date} · {selected.approval_status}</div>
            <EvidenceBadge confidence={selected.confidence} />

            <div className="flex gap-2 mt-6">
              {selected.script_id ? (
                <button data-testid="open-script-btn" onClick={() => window.location.href = `/scripts/${selected.script_id}`}
                  className="flex-1 px-4 py-2 rounded font-semibold text-sm text-neutral-900" style={{ background: "#D4FF00" }}>
                  Open script
                </button>
              ) : (
                <button data-testid="gen-script-from-cal-btn" onClick={() => generateScript(selected)} disabled={busyId === selected.id}
                  className="flex-1 px-4 py-2 rounded font-semibold text-sm text-neutral-900 flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "#D4FF00" }}>
                  {busyId === selected.id ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {busyId === selected.id ? "Generating…" : "Generate script"}
                </button>
              )}
              <button data-testid="delete-entry-btn" onClick={() => remove(selected.id)}
                className="px-4 py-2 rounded font-semibold text-sm border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
